import { getSVG } from '@/components/Common/styles/mixins';
import SunnyLayout from '@/components/Common/SunnyLayout/SunnyLayout';
import { SunnyFC } from '@/components/Common/SunnyNext';
import { withSunnyPage } from '@/components/Common/SunnyPage';
import NolPromotionBody from '@/components/NolPromotion/NolPromotionBody';
import NolPromotionNav from '@/components/NolPromotion/NolPromotionNav';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { generatePageMeta } from '@/services/SEOService/metaGenerator';
import { Braze, Kinesis } from '@/services/TrackerService';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { NolPromotionStore, NolPromotionStoreCtx } from '@/stores/NolPromotionStore';
import { getPageUrl, getRunMode } from '@/utils';
import { getCI } from '@/utils/domain/ci';
import { EN_APP_DEVICE, getAppDevice, getDevice } from '@/utils/functions/device';
import { useScrollStore } from '@/utils/hooks';
import qs from 'qs';
import { F, prop } from 'ramda';
import React, { useContext, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';

interface INolPromotionProps {
  isLoggedIn: boolean;
  hasCI: boolean;
  promotionUrl: string;
  promotionDeeplink: string;
  homeUrl: string;
  myYanoljaPath: string;
  loginPath: string;
  eventcode: string;
  hashedMemberNo: string;
  hyCardPath: string;
  returnUrl: string;
  marketingPrivacy: boolean;
  marketingPush: boolean;
  deviceOS: 'MW' | 'PW' | 'IOS' | 'AND';
  plccSplitSite: string;
  nickname: string;
  plccInterparkSite: string;
  plccTripleSite: string;
  tab: string;
  version: string;
  rcno: string;
  nolPointTermsUrl: string;
}

const NolPromotion: SunnyFC<INolPromotionProps> = (props) => {
  const globalStore = useContext(GlobalStoreCtx);
  const store = useMemo(() => {
    return new NolPromotionStore({
      ...props,
      globalStore,
      marketingTermsProps: {
        marketingPrivacy: props.marketingPrivacy,
        marketingPush: props.marketingPush,
        toastStore: globalStore.toast,
        deviceOS: props.device ? 'MW' : 'PW',
      },
    });
  }, []);

  useScrollStore();

  useEffect(() => {
    try {
      // position 범위는 1 ~ 3 이고 탭은 0 ~ 2 이기 때문에 1 만큼 차감해서 보정해준다.
      const position = Number(props.tab);

      if (position >= 1 && position <= 4) {
        store.onVersion3TabClick(position - 1);
      }
    } catch (e) {}
    void Kinesis.view(store.pageName, {
      referrer: store.eventcode,
      hashedHyundaicardMember: store.rcno,
      motherPageVersion: store.version,
    });
    Braze.sendCustomEvent('view_nolcard_page');
  }, []);
  return (
    <NolPromotionStoreCtx.Provider value={store}>
      <SunnyLayout>
        {{
          header: <NolPromotionNav />,
          body: <NolPromotionBody version={props.version} store={store} />,
        }}
      </SunnyLayout>
    </NolPromotionStoreCtx.Provider>
  );
};

NolPromotion.getInitialProps = async ({ req, res }) => {
  const isLoggedIn = req.session.isLoggedIn();
  const agreements = req.session.getAgreements();
  const certificated = agreements?.certification;

  const hasCI =
    isLoggedIn && certificated
      ? (await getCI(req)).map(prop('data')).caseOf({
          left: F,
          right: ({ ci }) => ci,
        })
      : false;

  const marketingPrivacy = isLoggedIn ? !!req.session.getAgreements()?.marketingPrivacy : false;
  const marketingPush = isLoggedIn ? !!req.session.getAgreements()?.marketingPush : false;
  const HY_EVENTCODE_KEY = 'hy-eventcode';

  // eventCode가 'undefined'로 저장되어 있는 케이스를 확인하여 로직 추가
  const cookieEventCode = req.cookies[HY_EVENTCODE_KEY] !== 'undefined' ? req.cookies[HY_EVENTCODE_KEY] : null;
  const eventcode = cookieEventCode || req.query.eventcode || req.query.eventCode || 'NOY92';
  const tab = req.query.tab;
  const query = qs.stringify({
    ...(req.query || {}),
    eventcode,
  });

  // deeplink 생성을 위해 환경별 home 주소 획득
  const homeUrl = req.config.Server.href();
  const promotionUrl = `${homeUrl}/promotion/nol-promotion?eventcode=${eventcode}`;
  const myYanoljaPath = `${homeUrl}/mypage`;
  const loginPath = `${homeUrl}/login?redirect=/promotion/nol-promotion`;

  /* 현대카드 추천인 id */
  const rcno = req.query.rcno || req.cookies['rcno'];
  if (req.query.rcno) {
    res.cookie('rcno', req.query.rcno, {
      maxAge: 1000 * 60 * 60 * 12, // 12시간
      domain: '.yanolja.com',
    });
  }

  if (!cookieEventCode) {
    res.cookie(HY_EVENTCODE_KEY, eventcode, {
      maxAge: 1000 * 60 * 60 * 12, // 12시간
      domain: '.yanolja.com',
    });
  }

  const promotionDeeplink = `wv?url=${homeUrl}/promotion/nol-promotion?${query}`;
  const hyCardPath = req.config.HyCardSite.href();
  const hashedMemberNo = req.session.hashedMemberNo;
  const returnUrl = `${homeUrl}/hooks/nol-promotion`;
  const appDevice = getAppDevice(req);
  const device = getDevice(req);
  const deviceOS =
    appDevice === EN_APP_DEVICE.IOS
      ? 'IOS'
      : appDevice === EN_APP_DEVICE.ANDROID
      ? 'AND'
      : device !== null
      ? 'MW'
      : 'PW';
  const plccSplitSite = req.config.PLCCSplitSite.href();
  const plccInterparkSite = req.config.PLCCInterParkSite.href();
  const plccTripleSite = req.config.PLCCTripleSite.href();
  const nickname = req.session.getNickname();
  const nolPointTermsUrl = req.config.Policy.path('/pf/point/nol/service').href();

  /**
   * 구버전: '1',
   * 6월 개편 버전: '2';
   * 9월 개편 버전: '3';
   * 10월 5일 개편 버전: '4';
   * 11월 21일 개편 버전: '5';
   * 12월 5일 개편 버전: '6';
   * 24년 1월 3일 개편 버전: '7';
   * 24/03/05: '8';
   * 24/04/02: '9';
   * 24/04/11: '10';
   * 24/04/30: '11';
   * 24/06/04: '12';
   * 24/06/10: '13';
   * 24/07/02: '14';
   * 24/07/23: '15';
   * 24/09/03: '16';
   * 24/09/24: '17';
   * 24/11/12: '18';
   * 24/11/26: '19';
   * 24/12/24: '20';
   * 25/02/04: '21';
   * 25/04/01: '22';
   * 25/04/21: '23';
   * 25/05/27: '24';
   * 25/07/29: '25';
   * 25/09/30: '26';
   * 25/11/25: '27';
   * 26/01/13: '28';
   * 26/03/10: '29';
   * 26/04/28: '30';
   * 26/06/23: '31';
   * 26/08/25: '32';
   */
  const nowMoment = moment(Date.now()).tz('Asia/Seoul');
  let version = '28';
  if (nowMoment.isAfter('2026-03-10 09:00:00')) {
    version = '29';
  }
  if (nowMoment.isAfter('2026-04-28 09:00:00')) {
    version = '30';
  }
  if (nowMoment.isAfter('2026-06-23 09:00:00')) {
    version = '31';
  }
  if (nowMoment.isAfter('2026-08-25 09:00:00')) {
    version = '32';
  }
  // DEV/QA에서 버전 직접 지정
  if (getRunMode() !== 'production' && !!req.query.version) {
    version = req.query.version as string;
  }

  const headProps = generatePageMeta({
    pageName: EN_PAGE_NAME.PAGE__PROMOTION__NOL_V7,
    pageUrl: getPageUrl(req),
    options: {
      image: getSVG('/static/images/nol-promotion/v7/meta-img-rectangle.png'),
    },
  });

  return {
    headProps,
    hasCI,
    isLoggedIn,
    homeUrl,
    promotionUrl,
    promotionDeeplink,
    myYanoljaPath,
    loginPath,
    eventcode,
    hashedMemberNo,
    returnUrl,
    marketingPrivacy,
    marketingPush,
    deviceOS,
    plccSplitSite,
    plccInterparkSite,
    plccTripleSite,
    nickname,
    tab,
    version,
    rcno,
    nolPointTermsUrl,
    hyCardPath,
  };
};

export default withSunnyPage({
  smartBanner: ({ homeUrl, eventcode }) => {
    return {
      path: `wv?url=${homeUrl}/promotion/nol-promotion`,
      params: {
        eventcode,
      },
    };
  },
})(NolPromotion);
